import { getFirestore, updateDoc } from "@firebase/firestore";
import { BiCalendarEdit } from "@react-icons/all-files/bi/BiCalendarEdit";
import { BiHourglass } from "@react-icons/all-files/bi/BiHourglass";
import { BsAspectRatio } from "@react-icons/all-files/bs/BsAspectRatio";
import { CgPin } from "@react-icons/all-files/cg/CgPin";
import { CgSize } from "@react-icons/all-files/cg/CgSize";
import { FaAudioDescription } from "@react-icons/all-files/fa/FaAudioDescription";
import { FaExpand } from "@react-icons/all-files/fa/FaExpand";
import { FaPause } from "@react-icons/all-files/fa/FaPause";
import { FaPlay } from "@react-icons/all-files/fa/FaPlay";
import { FaUserAstronaut } from "@react-icons/all-files/fa/FaUserAstronaut";
import { FaVolumeMute } from "@react-icons/all-files/fa/FaVolumeMute";
import { FaVolumeUp } from "@react-icons/all-files/fa/FaVolumeUp";
import { VscDebugRestart } from "@react-icons/all-files/vsc/VscDebugRestart";
import dayjs from "dayjs";
import {
  GetStaticPaths,
  GetStaticPropsContext,
  InferGetStaticPropsType,
} from "next";
import { withAuthUser } from "next-firebase-auth";
import { useTranslations } from "next-intl";
import { NextSeo } from "next-seo";
import { useRouter } from "next/router";
import dynamic from "next/dynamic";
import Head from "next/head";
import Link from "next/link";
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useFullscreen } from "react-use";
import { useForm, usePlugins } from "tinacms";
import { Countdown, CurrentBid } from "../../common/components/AuctionInfo";
import Avatar from "../../common/components/Avatar";
import Button from "../../common/components/Button";
import Header from "../../common/components/Header";
import { MediaComponent } from "../../common/components/Media";
import { Arts } from "../../common/lib/api";
import { Art, Collection, LOCALE_OPTIONS } from "../../common/lib/api/base";
import { SPACESHIP_HASH } from "../../common/lib/constants";
import { getCachedRates } from "../../common/lib/getCryptoRates";
import { useRealtime } from "../../common/lib/hooks/useRealtime";
import { useTTS } from "../../common/lib/hooks/useTTS";
import { useBreakpoint } from "../../common/lib/hooks/useWindow";
import { makeAdmin } from "../../common/lib/makeAdmin";
import { makeFirebase } from "../../common/lib/makeFirebase";
import {
  cs,
  fromSeconds,
  getArtLowQualUrl,
  getArtUrl,
  getArtOriginalUrl,
  makeStaticPathsLocale,
  MapOf,
  removeInvalidValues,
  toDocs,
  isVideo,
} from "../../common/lib/utils";
import { ART_FORM_RESULT, makeArtForm } from "../../forms/artForm";
import styles from "../../styles/Arts.module.scss";

const FullscreenImage = dynamic(
  () => import("../../common/components/FullscreenImage")
);

type TRANSFORM_MAP = MapOf<(i: string, ...args: Array<any>) => any>;

type TYPE_MAP = MapOf<JSX.Element>;

export const getStaticPaths: GetStaticPaths = async () => {
  try {
    const admin = await makeAdmin();
    const store = admin.firestore();
    const ids = await toDocs<{ id: string; slug: string }>(
      // @ts-ignore
      store.collection("arts").select("slug").get()
    );
    return {
      paths: makeStaticPathsLocale(
        ids.map(({ slug }) => ({
          params: {
            slug: slug,
          },
        }))
      ),
      fallback: "blocking",
    };
  } catch (e) {
    return {
      paths: [],
      fallback: "blocking",
    };
  }
};

export const getStaticProps = async (ctx: GetStaticPropsContext) => {
  const locale = ctx.locale as LOCALE_OPTIONS;
  const slug = ctx.params?.slug;
  const firebase = await makeAdmin();
  const firestore = firebase.firestore();
  const arts = firestore.collection("arts");
  const artists = firestore.collection("collections");
  const [[art], crypto] = await Promise.all([
    toDocs<Art>(arts.where("slug", "==", slug).get()),
    getCachedRates(),
  ]);
  const [[collection], collabs] = await Promise.all([
    toDocs<Collection>(artists.where("slug", "==", art.collection.slug).get()),
    toDocs<{ artist: string; type: string }>(
      firestore.collectionGroup("collabs").where("art", "==", art.id).get()
    ).then((incArtists) => {
      const artistToTypeMap = incArtists.reduce<{ [key: string]: string }>(
        (prev, curr) => {
          prev[curr.artist] = curr.type;
          return prev;
        },
        {}
      );
      const artistArray = incArtists.map(({ artist }) => artist);
      if (artistArray.length > 0)
        return toDocs<Collection>(
          artists.where("slug", "in", artistArray).get()
        ).then((artists) =>
          artists.map((artist) => ({
            ...artist,
            ...artist.overrides?.[locale],
            collab_type: artistToTypeMap[artist.id],
          }))
        );
      return [];
    }),
  ]);
  const isTranslated = !!art.overrides?.[locale];
  return {
    props: {
      art: {
        ...art,
        ...art.overrides?.[locale],
        collection: { ...collection, ...collection.overrides?.[locale] },
      },
      crypto,
      isTranslated,
      collabs,
    },
    revalidate: 10,
  };
};

const TRAIT_TYPE_ICON_MAP: TYPE_MAP = {
  dimensions: <BsAspectRatio />,
  style: <CgSize />,
  duration: <BiHourglass />,
  location: <CgPin />,
  created_at: <BiCalendarEdit style={{ marginBottom: 1 }} />,
  author: <FaUserAstronaut />,
};

const identity = <T extends any>(i: T) => i;
const TRAIT_TYPE_TRANSFORMS: TRANSFORM_MAP = {
  duration: (dur) => fromSeconds(+dur),
  created_at: (date, locale: LOCALE_OPTIONS) =>
    dayjs(date, "MM/DD/YYYY").format(
      locale === "en" ? "MMMM Do, YYYY" : "D [de] MMMM, YYYY"
    ),
};

const TRAIT_ORDER_MAP: MapOf<number> = {
  author: 0,
  style: 1,
  dimensions: 2,
  duration: 3,
  location: 4,
  created_at: 5,
};
type ART_PAGE_PROPS = InferGetStaticPropsType<typeof getStaticProps>;

const TTSControls: React.FC<{
  text?: string;
  isTranslated: boolean;
  isMuted: boolean;
  toggleMute: () => void;
}> = ({ text, isTranslated, isMuted, toggleMute }) => {
  const { isPlaying, play, pause, resume, utterance } = useTTS(
    text || "",
    isTranslated
  );
  const t = useTranslations("Art");
  console.log({ text, utterance });
  if (!text || !utterance) return null;
  const isPaused = isPlaying === null;
  if (isPlaying === false) {
    return (
      <button
        aria-label={t("audioDescription") as string}
        title={t("playAudio") as string}
        onClick={() => {
          if (!isMuted) toggleMute();
          play();
        }}
      >
        <FaAudioDescription />
      </button>
    );
  }
  const ButtonToShow = isPaused ? FaPlay : FaPause;
  return (
    <>
      <button
        aria-label={t("restartAudio") as string}
        title={t("restartAudio") as string}
        onClick={() => {
          play();
        }}
      >
        <VscDebugRestart />
      </button>
      <button
        aria-label={t("pauseAudio") as string}
        title={t("pauseAudio") as string}
        onClick={() => {
          if (!isPaused) pause();
          else resume();
        }}
      >
        <ButtonToShow style={{ fontSize: isPaused ? `0.9em` : undefined }} />
      </button>
    </>
  );
};

const ArtPage: React.FC<ART_PAGE_PROPS> = (props) => {
  const { art: propsArt, crypto, isTranslated, collabs } = props;
  if (!propsArt || !crypto)
    return (
      <>
        <Head>
          <title>Not Found</title>
        </Head>
        <main>
          <h1>The page you requested does not exist</h1>
          <p>
            <a href="/">Click here to go back to the home page</a>
          </p>
        </main>
      </>
    );
  const router = useRouter();
  const { locale: incLocale, query } = router;
  const locale = (incLocale || "en") as LOCALE_OPTIONS;
  const [data, form] = useForm<ART_FORM_RESULT>(
    makeArtForm(propsArt, {
      onSubmit: async (formData) => {
        const { reserve_price, ...rest } = formData;
        const arts = new Arts(getFirestore(makeFirebase()));
        await updateDoc(arts.artAt(art.id), {
          [`overrides.${locale}`]: removeInvalidValues(rest),
          reserve_price,
        });
        window.location.reload();
      },
    })
  );
  usePlugins(form);
  const { data: realtimeArt } = useRealtime<Art>(
    `arts/${propsArt?.id}`,
    propsArt
  );
  const art = {
    ...propsArt,
    ...realtimeArt,
    ...realtimeArt.overrides?.[locale],
    ...data,
  };
  const [isMuted, setIsMuted] = useState(true);
  const toggleFullscreen = () => {
    router.push({
      href: router.asPath,
      query: {
        ...query,
        show: true,
      },
    });
  };
  const isMedium = useBreakpoint("MEDIUM");
  // Keep the const even if unused, it acts weird if you dont
  const t = useTranslations("Art");
  const traits: NonNullable<Art["traits"]> = useMemo(
    () =>
      art?.traits
        ? art.traits.sort(
            (a, b) =>
              TRAIT_ORDER_MAP[a.trait_type] - TRAIT_ORDER_MAP[b.trait_type]
          )
        : [],
    [art]
  );
  const toggleMute = () => setIsMuted(!isMuted);

  const {
    animation_url,
    animation_original_url,
    name,
    description,
    audio_description,
  } = art;
  const bio = art.collection.description;
  const endOfSentenceRegex = /[\.\!\?]/g;
  // Run twice, keep second result
  const lastOfSecondSentence =
    (endOfSentenceRegex.exec(bio), endOfSentenceRegex.exec(bio));
  const originalUrl = getArtOriginalUrl(art);
  return (
    <>
      <NextSeo title={art.name} />
      <Header className={styles.header} />
      <div className={styles.image_container}>
        <MediaComponent
          src={getArtUrl(art)}
          placeholder={getArtLowQualUrl(art)}
          onClick={toggleFullscreen}
          className={styles.original_image}
          muted={isMuted}
          alt={art.alt_text}
        />
        <div className={styles.buttons_container}>
          {(animation_original_url || animation_url) && (
            <button
              aria-label={t("unmute") as string}
              title={t(isMuted ? "unmute" : "mute") as string}
            >
              {isMuted ? (
                <FaVolumeMute onClick={toggleMute} />
              ) : (
                <FaVolumeUp onClick={toggleMute} />
              )}
            </button>
          )}
          {isMedium && (
            <TTSControls
              text={audio_description}
              {...{ toggleMute, isMuted, isTranslated: !!isTranslated }}
            />
          )}
          <button>
            <FaExpand onClick={toggleFullscreen} />
          </button>
        </div>
      </div>
      {query?.show === "true" && (
        <FullscreenImage
          src={originalUrl}
          {...{ t }}
          onClickaway={() => router.back()}
        />
      )}
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={cs(styles, ["grid", "title_and_artist"])}>
            <h1 className={styles.art_title}>{name}</h1>
            <Link href={`/artists/${art.collection.slug}`}>
              <a>
                <div
                  className={cs(styles, ["top_name_wrapper", "artist_info"])}
                >
                  <Avatar
                    src={art.collection.image_url}
                    size={42}
                    className={styles.avatar_black}
                  />
                  <div className={cs(styles, ["row_center", "top_text"])}>
                    <span>{t("created_by")}</span>
                    {art.collection.name.split(" ").map((i, n) => (
                      <h2 key={n}>{i}</h2>
                    ))}
                  </div>
                </div>
              </a>
            </Link>
          </div>
          <div className={styles.grid}>
            <div>
              <h2>{t("about")}</h2>
              <div className={styles.traits}>
                {traits
                  .map((trait) => {
                    trait.trait_type = trait.trait_type.trim().toLowerCase();
                    return trait;
                  })
                  .filter((trait) =>
                    Boolean(TRAIT_TYPE_ICON_MAP[trait.trait_type])
                  )
                  .map((trait, i) => {
                    const Icon = TRAIT_TYPE_ICON_MAP[trait.trait_type];
                    const transform =
                      TRAIT_TYPE_TRANSFORMS[trait.trait_type] || identity;
                    const title = t(trait.trait_type);
                    return (
                      <div key={trait.trait_type + i}>
                        {Icon}
                        <p>
                          <span>{title}</span>
                          {transform(trait.value, locale)}
                        </p>
                      </div>
                    );
                  })}
              </div>
              <p className={styles.edition}>
                {t("edition", {
                  num: 1,
                  total: 1,
                  span1: (c) => <span key={1}>{c}</span>,
                  span2: (c) => <span key={2}>{c}</span>,
                })}
              </p>
              {collabs && collabs.length > 0 && (
                <>
                  <h2>Collabs</h2>
                  <div className={styles.traits} style={{ fontSize: "1.2em" }}>
                    {collabs.map((collabArtist) => {
                      return (
                        <Link href={`/artists/${collabArtist.slug}`}>
                          <a>
                            <div>
                              <Avatar
                                src={collabArtist.image_url}
                                size={44}
                                className={cs(styles, [
                                  "avatar_black",
                                  "avatar_collab",
                                ])}
                              />
                              <p style={{ marginTop: 2, marginLeft: 4 }}>
                                <span>{t(collabArtist.collab_type)}</span>
                                {collabArtist.name}
                              </p>
                            </div>
                          </a>
                        </Link>
                      );
                    })}
                  </div>
                </>
              )}
              {/* <a
                href={`https://etherscan.io/token/${
                  art.asset_contract.address
                }?a=${art.token_id.toString()}`}
              >
                <button>See on Etherscan</button>
              </a> */}
            </div>
            <div style={{ textAlign: "justify" }}>
              <h2>{t("description")}</h2>
              <ReactMarkdown className={styles.art_description}>
                {description}
              </ReactMarkdown>
            </div>
            <div className={styles.bid_wrapper}>
              <CurrentBid
                {...{ art, quote: crypto.ETH.quote.USD.price }}
                titleComponent={(props) => (
                  <h2
                    {...props}
                    style={{ fontSize: "1.5em", margin: "0.83em 0 " }}
                  />
                )}
                variant={"black-filled"}
              />
              {isMedium && (
                <a
                  href={art.permalink + `?ref=${SPACESHIP_HASH}`}
                  rel="noopener noreferrer"
                  target={"_blank"}
                >
                  <Button
                    title={t(art.reserve_price ? "bid" : "offer") as string}
                    variant="black-filled"
                  />
                </a>
              )}
            </div>
            <div>
              {art.sell_orders && art.sell_orders.length > 0 && (
                <>
                  <h2>Auction ends in</h2>
                  <div className={styles.countdown_wrapper}>
                    <div>
                      <Countdown
                        title={null}
                        closing_date={art.sell_orders?.[0]?.closing_date}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
          <div>
            <div>
              <h2 style={{ marginBottom: "0.5em" }}>{t("artist")}</h2>
            </div>
            <div />
            <div className={styles.artist_info} style={{ fontSize: "1.5em" }}>
              <Avatar
                src={art.collection.image_url}
                size={isMedium ? 52 : 42}
                className={styles.avatar_black}
              />
              <div
                className={styles.row_center}
                style={{
                  marginLeft: 10,
                  flex: 1,
                  justifyContent: "flex-start",
                }}
              >
                <Link href={`/artists/${art.collection.slug}`}>
                  <a>
                    {art.collection.name.split(" ").map((i, n) => (
                      <h3 key={n}>{i}</h3>
                    ))}
                  </a>
                </Link>
              </div>
            </div>
            <div className={styles.biography}>
              <h3 style={{ textTransform: "none" }}>{t("bio")}</h3>
              {lastOfSecondSentence ? (
                <>
                  <ReactMarkdown>
                    {propsArt.collection.description.slice(
                      0,
                      lastOfSecondSentence.index
                    ) + "..."}
                  </ReactMarkdown>
                  <Link href={`/artists/${propsArt.collection.slug}`}>
                    <a>
                      <h3>{t("continueReading")}</h3>
                    </a>
                  </Link>
                </>
              ) : (
                <ReactMarkdown>{propsArt.collection.description}</ReactMarkdown>
              )}
            </div>
          </div>
          {/* Fouth cell */}
        </div>
      </div>
    </>
  );
};

export default withAuthUser<ART_PAGE_PROPS>()(ArtPage);
